import { productCardHelper } from '@/components/common/product-card/helper'
import { getValue, groupBy } from '@/lib/utils'
import {
  BreadCrumbItem,
  CoolClubSaleFormatted,
  ETags,
  GroupedVariants,
  Option,
  Product,
  ProductCollectionResponse,
  ProductFormatted,
  ProductResponseFormatted,
  ProductVariant,
  StupidProduct,
  VariantMapped,
} from '@/types/product'
import {
  ExtraAttribute,
  ExtraFeature,
  ExtraImage,
  ExtraThumbDescription,
  ProductDetail,
  ProductExtra,
  ProductPromotion,
} from '@/types/product-detail'

const MAX_QUANTITY_PRODUCT = 5
const MIN_PRICE_FREE_SHIPPING = 200000

const reFormatProductsByCollectionResponse = (
  data: ProductCollectionResponse
): ProductResponseFormatted => {
  const products = data?.products?.data
    ?.map((product) => {
      const colorOptions =
        product.options_value.find((option) => {
          return option?.options_id?.includes('color')
        })?.options || ([] as Option[])

      const isCoolClubSale = product?.coolclub_sale
      const coolClubSale: CoolClubSaleFormatted = {
        active: false,
        coolclub_price: 0,
        rankings: [],
      }

      if (
        isCoolClubSale &&
        isCoolClubSale?.coolclub_price < product?.regular_price
      ) {
        coolClubSale.active = true
        coolClubSale.coolclub_price = isCoolClubSale?.coolclub_price
        coolClubSale.rankings = isCoolClubSale?.rankings
      }

      const { regular_price, compare_price, sale_percent } =
        productCardHelper.getPrices(product)

      const mappedVariants = transformVariantMapped(
        product,
        product.variants,
        colorOptions
      )

      const firstColor = Object.keys(mappedVariants)[0]
      const firstVariant = mappedVariants[firstColor]?.[0] || null
      const isFreeShipping = product?.regular_price >= MIN_PRICE_FREE_SHIPPING

      return {
        title: product.title,
        href: product.seo_alias.includes('product')
          ? product.seo_alias
          : `/product/${product.seo_alias}`,
        is_combo: product.is_combo,
        id: product.id,
        regular_price,
        compare_price,
        sale_percent,
        note: product.addon_note,
        active: Boolean(Object.keys(mappedVariants).length),
        mapped_variants: mappedVariants,
        icon_thumbnail: product?.icon_thumbnail,
        default_thumbnail: firstVariant?.thumbnail,
        default_thumbnail_hover: firstVariant?.thumbnail_hover,
        tags: getTags(product?.tags, product),
        is_free_shipping: isFreeShipping,
        is_quick_add: !firstVariant?.size,
        collections: product.collections,
        coolclub_sale: coolClubSale,
        review: product.review,

        // TODO: bắt be thêm key isX2CoolCash vào product, hiện tại con cũ lấy từ api 1 list sản phẩm ra rùi includes rất là đần
      } as ProductFormatted
    })
    .filter((item) => item?.active)

  return {
    data: products,
    is_next_page: data?.products?.current_page < data?.products?.last_page,
    last_page: data?.products?.last_page,
    current_page: data?.products?.current_page,
    total: data?.products?.total,
    // htmlSeoContent: data?.htmlSeoContent,
    titleDisplay: data?.titleDisplay,
    seo_alias: data?.detail?.seo_alias,
    title: data?.detail?.seo_title,
  }
}

const transformComboVariantMapped = (
  product: Product,
  variants: ProductVariant[]
) => {
  const availableVariants = variants.filter((variant) => {
    if (variant?.hide) return false
    if (
      product?.apply_allowance_inventory &&
      variant.quantity <= MAX_QUANTITY_PRODUCT
    ) {
      return false
    }
    return true
  })

  return availableVariants.map((variant) => {
    return {
      id: variant.id,
      option1: variant.option1 || '',
      option2: variant.option2 || '',
      isDisable: !variant.available,
      regular_price: variant.regular_price,
      title: variant.title,
      quantity: variant.quantity,
    }
  })
}

const transformVariantNoColorMapped = (
  product: Product,
  variants: ProductVariant[] = []
): GroupedVariants => {
  const availableVariants = variants.filter((variant) => {
    if (variant?.hide) return false
    if (
      product?.apply_allowance_inventory &&
      variant.quantity <= MAX_QUANTITY_PRODUCT
    ) {
      return false
    }
    return true
  })

  if (availableVariants.length === 0) return {}

  // const groupVariant = groupBy(availableVariants, (item) => item.option1)

  // const updatedGroupVariant: GroupedVariants = Object.fromEntries(
  //   Object.entries(groupVariant).map(([key, items]) => [
  //     key,
  //     (items ?? []).map((variant) => ({
  //       id: variant.id,
  //       product_id: variant.product_id,
  //       size: variant.option2 || '',
  //       color: variant.option1 || '',
  //       quantity: variant.quantity,
  //       thumbnail: getValue(product, 'images.0.src', ''),
  //       thumbnail_hover: getValue(product, 'images.1.src', ''),
  //       colorImage: getValue(product, 'images.1.src', ''),
  //       isDisable: !variant.available,
  //       regular_price: variant.regular_price,
  //       title: variant.title,
  //     })),
  //   ])
  // )

  const updatedGroupVariant = {
    FAKE: availableVariants.map((variant) => ({
      id: variant.id,
      product_id: variant.product_id,
      size: variant.option1 || '',
      color: '',
      quantity: variant.quantity,
      thumbnail: getValue(product, 'images.0.src', ''),
      thumbnail_hover: getValue(product, 'images.1.src', ''),
      colorImage: getValue(product, 'images.1.src', ''),
      isDisable: !variant.available,
      regular_price: variant.regular_price,
      title: variant.title,
    })),
  }

  return updatedGroupVariant
}

export const transformVariantMapped = (
  product: Product,
  variants: ProductVariant[] = [],
  colorOptions: Option[],
  isPreview?: boolean
): GroupedVariants => {
  if (colorOptions?.length === 0) {
    // nếu không có màu nhưng có variants thì map từ variants
    if (variants?.length > 0) {
      return transformVariantNoColorMapped(product, variants)
    }

    return {}
  }

  const colorOptionsHashMap = new Map(
    colorOptions?.map((item) => [item.title, item])
  )

  console.log('isPreview', isPreview)

  const availableVariant = variants?.filter((variant) => {
    if (isPreview) {
      return true
    }
    // nếu tích ẩn biến thể mặc dù tồn còn nhiều thì size cũng phải ẩn , nếu tích ẩn size thì màu đấy cũng ẩn luôn
    if (variant?.hide) return false

    //nếu tích "Áp dụng ngưỡng lưu kho tối thiểu (Chỉ bán nếu tồn kho lớn hơn 5)" đến hết thì phải hiển thị
    if (
      product?.apply_allowance_inventory &&
      variant.quantity <= MAX_QUANTITY_PRODUCT
    ) {
      return false
    }
    return true
  })

  if (availableVariant.length === 0) return {}

  const groupVariant = groupBy(availableVariant, (item) => item.option1)

  const updatedGroupVariant: GroupedVariants = Object.fromEntries(
    Object.entries(groupVariant)
      // Bỏ key nếu không tồn tại trong colorOptionsHashMap
      .filter(([key]) => colorOptionsHashMap.has(key))
      .map(([key, items]) => [
        key,
        (items ?? []).map((item) => {
          const valueColor = colorOptionsHashMap.get(item?.option1)
          return {
            id: item.id,
            size: item?.option2,
            color: item?.option1,
            colorImage: getValue(
              valueColor?.image[valueColor?.image.length - 1],
              'src',
              ''
            ),
            quantity: item.quantity,
            isDisable: item.quantity <= 0,
            product_id: item.product_id,
            thumbnail:
              getValue(valueColor, 'image.0.src', '') ||
              product?.images[0]?.src,
            thumbnail_hover:
              getValue(valueColor, 'image.1.src', '') ||
              product?.images[1]?.src,
            regular_price: item.regular_price,
            title: item.title,
          }
        }),
      ])
      .filter((entry) => {
        const value = entry[1] as VariantMapped[]
        if (isPreview) return true
        // Hide if only 1 variant and it's disabled
        if (value.length === 1 && value[0].isDisable) return false
        // Hide if all variants are disabled
        if (value.every((variant) => variant.isDisable)) return false
        return true
      })
  )
  return updatedGroupVariant
}

const getTags = (tags: Record<string, string>, product: Product) => {
  if (product.preorder && product.addon_note) {
    return [ETags.PREORDER]
  }
  const validTags = Object.values(tags).filter((tag) => tag !== 'cleandye')

  if (validTags.length > 0) {
    const firstTag = validTags[0]

    if (product._id === '650428bfec1126721240dcb4') {
      return [ETags.LIMITED_QUANTITY]
    }

    return [firstTag]
  }

  return validTags
}

// Helper function to validate ExtraAttribute
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isValidExtraAttribute = (attr: any): attr is ExtraAttribute => {
  return (
    typeof attr === 'object' &&
    attr !== null &&
    ((typeof attr.key === 'string' && attr.key.trim() !== '') ||
      (typeof attr.id === 'string' && attr.id.trim() !== '')) &&
    typeof attr.title === 'string' &&
    attr.title.trim() !== '' &&
    typeof attr.src_icon === 'string' &&
    attr.src_icon.trim() !== ''
  )
}

// Helper function to validate ExtraImage
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isValidExtraImage = (img: any): img is ExtraImage => {
  return (
    typeof img === 'object' &&
    img !== null &&
    typeof img.src_img === 'string' &&
    img.src_img.trim() !== ''
  )
}

// Helper function to validate ExtraFeature
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isValidExtraFeature = (feature: any): feature is ExtraFeature => {
  return (
    typeof feature === 'object' &&
    feature !== null &&
    typeof feature.title === 'string' &&
    feature.title.trim() !== '' &&
    Array.isArray(feature.value) &&
    feature.value.length > 0 &&
    // Kiểm tra xem mỗi item trong value có title hợp lệ không
    feature.value.some(
      (item: { title?: string }) =>
        typeof item === 'object' &&
        item !== null &&
        typeof item.title === 'string' &&
        item.title.trim() !== ''
    )
  )
}

// Helper function to validate ExtraThumbDescription
const isValidExtraThumbDescription = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  thumb: any
): thumb is ExtraThumbDescription => {
  return (
    typeof thumb === 'object' &&
    thumb !== null &&
    typeof thumb.src_img === 'string' &&
    thumb.src_img.trim() !== ''
  )
}
const validateBreadCrumb = (bread_crumb: unknown): BreadCrumbItem[] => {
  if (Array.isArray(bread_crumb)) {
    return bread_crumb
  }

  if (
    typeof bread_crumb === 'object' &&
    bread_crumb !== null &&
    !Array.isArray(bread_crumb)
  ) {
    return Object.values(bread_crumb)
  }

  return []
}

// Validate and normalize ProductExtra
const validateProductExtra = (extra?: ProductExtra): ProductExtra => {
  if (!extra || typeof extra !== 'object') {
    return {
      p_extra_attributes: [],
      p_extra_images: [],
      p_extra_features: [],
      p_extra_banner_section_tech: [],
      p_extra_short_description: '',
      p_extra_thumb_description: { src_img: '', src_alt: '' },
      p_extra_description: { src_desktop: '', src_mobile: '', src_alt: '' },
      p_extra_support_movement: '',
    }
  }

  return {
    // Mảng
    p_extra_attributes: Array.isArray(extra.p_extra_attributes)
      ? extra.p_extra_attributes.filter(isValidExtraAttribute)
      : [],

    p_extra_images: Array.isArray(extra.p_extra_images)
      ? extra.p_extra_images.filter(isValidExtraImage)
      : [],

    p_extra_features: Array.isArray(extra.p_extra_features)
      ? extra.p_extra_features.filter(isValidExtraFeature)
      : [],

    p_extra_banner_section_tech: Array.isArray(
      extra.p_extra_banner_section_tech
    )
      ? extra.p_extra_banner_section_tech.filter(
          (item) => typeof item === 'object' && item !== null
        )
      : [],

    // Chuỗi
    p_extra_short_description:
      typeof extra.p_extra_short_description === 'string'
        ? extra.p_extra_short_description
        : '',

    p_extra_support_movement:
      typeof extra.p_extra_support_movement === 'string'
        ? extra.p_extra_support_movement
        : '',

    // Object
    p_extra_thumb_description: isValidExtraThumbDescription(
      extra.p_extra_thumb_description
    )
      ? extra.p_extra_thumb_description
      : { src_img: '', src_alt: '' },

    p_extra_description:
      typeof extra.p_extra_description === 'object' &&
      extra.p_extra_description !== null
        ? extra.p_extra_description
        : { src_desktop: '', src_mobile: '', src_alt: '' },
  }
}

// const getPricingPolicy = (product: ProductDetail) => {
//   const { pricing_policy, collection_pricing_policy } = product
//   return {
//     pricing_policy,
//     collection_pricing_policy,
//   }
// }

const transformCombo = (data: ProductDetail) => {
  if (!data.is_combo) return null

  const colorOptions =
    data?.options_value.find((option) => {
      return option?.options_id?.includes('color')
    })?.options || ([] as Option[])
  const colorHashMap = new Map(colorOptions.map((item) => [item.title, item]))

  const options = data.options.map((item) => {
    return {
      title: item.title,
      values: item?.values.map((value) => ({
        label: value,
        value,
        image: colorHashMap.get(value)?.image[0]?.src || '',
      })),
      option_id: item?.option_id,
    }
  })

  return {
    options,
    variants: transformComboVariantMapped(data, data.variants),
  }
}

const reFormatProductDetailResponse = (
  data: ProductDetail,
  isPreview?: boolean
) => {
  const colorOptions =
    data?.options_value.find((option) => {
      return option?.options_id?.includes('color')
    })?.options || ([] as Option[])

  const { regular_price, compare_price, sale_percent } =
    productCardHelper.getPrices(data)

  const mappedVariants = transformVariantMapped(
    data,
    data.variants,
    colorOptions,
    isPreview
  )

  const combo = transformCombo(data)

  // Filter out color options that don't exist in mappedVariants
  const usedColors = Object.keys(mappedVariants)
  const filteredColorOptions = colorOptions.filter((option) =>
    usedColors.includes(option.title)
  )

  // Update color options in the product
  if (data.options_value) {
    const colorOptionIndex = data.options_value.findIndex((option) =>
      option?.options_id?.includes('color')
    )
    if (colorOptionIndex !== -1) {
      data.options_value[colorOptionIndex].options = filteredColorOptions
    }
  }

  // Validate and normalize extra data
  const validatedExtra = validateProductExtra(data.extra)

  // Get default thumbnail from first variant
  const firstColor = Object.keys(mappedVariants)[0]
  const firstVariant = mappedVariants[firstColor]?.[0] || null
  const default_thumbnail = firstVariant?.thumbnail || ''

  let promotionsActive: ProductPromotion[] = []

  if (data?.promotions?.length > 0) {
    promotionsActive = data?.promotions?.filter((item) => item.show_in_detail)
  }

  // const policy = getPricingPolicy(data)

  const coolClubSale = data?.coolclub_sale
  const coolClubSaleFormatted: CoolClubSaleFormatted = {
    active: false,
    coolclub_price: 0,
    rankings: [],
  }

  if (coolClubSale && coolClubSale?.regular_price < data?.regular_price) {
    coolClubSaleFormatted.active = true
    coolClubSaleFormatted.coolclub_price = coolClubSale?.regular_price
    coolClubSaleFormatted.rankings = coolClubSale?.flash_sale?.rankings
  }

  return {
    ...data,
    bread_crumb: validateBreadCrumb(data?.bread_crumb),
    regular_price,
    compare_price,
    sale_percent,
    mapped_variants: mappedVariants,
    color_options: filteredColorOptions,
    extra: validatedExtra,
    default_thumbnail,
    promotions: promotionsActive,
    coolclub_sale: coolClubSaleFormatted,
    combo,
  }
}

// =============================== Search Spotlight ===============================
const reFormatProductsSpotlightResponse = (data: StupidProduct[]) => {
  try {
    const products = data
      ?.map((product) => {
        const colorOptions =
          product.options_value.find((option) => {
            return option?.options_id?.includes('color')
          })?.options || ([] as Option[])

        const { regular_price, compare_price, sale_percent } =
          productCardHelper.getPrices(product)

        const mappedVariants = transformVariantMapped(
          product,
          product.variants,
          colorOptions
        )

        const firstColor = Object.keys(mappedVariants)[0]
        const firstVariant = mappedVariants[firstColor]?.[0] || null
        const isFreeShipping = product?.regular_price >= MIN_PRICE_FREE_SHIPPING

        return {
          title: product.title,
          href: product.seo_alias.includes('product')
            ? product.seo_alias
            : `/product/${product.seo_alias}`,
          is_combo: product.is_combo,
          id: product.id,
          regular_price,
          compare_price,
          sale_percent,
          note: product.addon_note,
          active: Boolean(Object.keys(mappedVariants).length),
          mapped_variants: mappedVariants,
          icon_thumbnail: product?.icon_thumbnail,
          default_thumbnail: firstVariant?.thumbnail,
          default_thumbnail_hover: firstVariant?.thumbnail_hover,
          tags: getTags(product?.tags, product),
          is_free_shipping: isFreeShipping,
          is_quick_add: !firstVariant?.size,
          // TODO: bắt be thêm key isX2CoolCash vào product, hiện tại con cũ lấy từ api 1 list sản phẩm ra rùi includes rất là đần
        }
      })
      .filter((item) => item?.active)

    return products
  } catch (error) {
    console.error('🧙 ~ error:dcmmmmmmmmmmmm', error)
    return []
  }
}

const reFormatProductResponse = (data: Product[]) => {
  if (!data || !Array.isArray(data)) return []

  return data.map((product) => {
    const colorOptions =
      product.options_value.find((option) => {
        return option?.options_id?.includes('color')
      })?.options || ([] as Option[])

    const { regular_price, compare_price, sale_percent } =
      productCardHelper.getPrices(product)

    const mappedVariants = transformVariantMapped(
      product,
      product.variants,
      colorOptions
    )

    const firstColor = Object.keys(mappedVariants)[0]
    const firstVariant = mappedVariants[firstColor]?.[0] || null

    return {
      id: product.id,
      display_name: product.title || product.display_name,
      display_name_open: product.display_name_open || '',
      seo_alias: product.seo_alias,
      regular_price,
      compare_price,
      percent: sale_percent,
      mapped_variants: mappedVariants,
      default_thumbnail: firstVariant?.thumbnail || product?.images[0].src,
      default_thumbnail_hover:
        firstVariant?.thumbnail_hover || product?.images[1].src,
      title: product.display_name,
      href: `/product/${product.seo_alias}`,
      is_combo: product.is_combo,
      note: product.addon_note,
      active: Boolean(Object.keys(mappedVariants).length),
      tags: getTags(product?.tags, product),
      is_free_shipping: product.regular_price >= MIN_PRICE_FREE_SHIPPING,
      is_quick_add: !firstVariant?.size,
      sale_percent: product.percent,
      images: product.images,
      options_value: product.options_value,
    }
  })
}

const reFormatProductPromotionResponse = (data: ProductPromotion[]) => {
  if (!data || !Array.isArray(data)) return []

  return data.map((product) => {
    const colorOptions =
      product.options.find((option) => option?.option_id?.includes('color'))
        ?.values || []

    const sizeOptions =
      product.options.find((option) => option?.option_id?.includes('size'))
        ?.values || []

    const thumbnail = product?.images?.[0]?.src || ''

    const formatColorOptions = colorOptions.map((color) => ({
      label: color,
      value: color,
    }))

    const formatSizeOptions = sizeOptions.map((size) => ({
      label: size,
      value: size,
    }))

    return {
      ...product,
      colorOptions: formatColorOptions,
      sizeOptions: formatSizeOptions,
      thumbnail,
    }
  })
}

export const ProductFormatter = {
  reFormatProductsByCollectionResponse,
  reFormatProductDetailResponse,
  reFormatProductsSpotlightResponse,
  reFormatProductResponse,
  reFormatProductPromotionResponse,
}
